import { forwardRef } from "react";
import {
  createBemBlockBuilder,
  useThrottledDebouncedInput,
} from "@ebsco-ui/utils";
import type { UseComboboxStateChange } from "downshift";
import Autocomplete from "../autocomplete";
import type {
  AutocompleteProps,
  Item as AutocompleteItem,
} from "../autocomplete";
import Adornment from "../adornment";
import { getWrapperClassNames } from "../common/utils";
import SearchButton, { SearchButtonProps } from "./search-button";
import {
  ExcludedAutocompleteProps,
  Group,
  GroupItemsBy,
  Item,
  SearchAutocompleteMessages,
  FocusableElements,
} from "./interfaces";
import { BASE_CLASS_NAME, MIN_SEARCH_LENGTH } from "./constants";
import BooleanChip, { BooleanChipProps } from "./boolean-chip/boolean-chip";
import ItemContent from "./item-content";
import { useGroupedBy } from "./hooks/use-grouped-by";

import "./search-autocomplete.scss";

export interface SearchAutocompleteProps
  extends Omit<AutocompleteProps, ExcludedAutocompleteProps> {
  className?: string;
  groups?: Group[];
  onFocus?: () => void;
  setFocusOn?: FocusableElements;
  groupItemsBy?: GroupItemsBy;
  baseClassName?: string;
  itemToString?: (item?: Item | null) => string;
  items: Item[];
  messages: SearchAutocompleteMessages;
  minSearchLength?: number;
  onChange?: (changes: UseComboboxStateChange<Item>) => void;
  fetchItems?: (inputValue: string) => void;
  displayHighlightedValue?: boolean;
  value?: string;
  hasSearchButton?: boolean;
  searchButtonProps?: SearchButtonProps;
  hasBooleanChip?: boolean;
  booleanChipProps?: BooleanChipProps;
  shouldHighlightItems?: boolean;
}

export const SearchAutocomplete = forwardRef<
  HTMLDivElement,
  SearchAutocompleteProps
>(
  (
    {
      baseClassName: consumerBaseClassName,
      className: consumerWrapperClassName,
      displayHighlightedValue,
      groups,
      groupItemsBy,
      fetchItems,
      itemToString = (item) => item?.term ?? "",
      items,
      minSearchLength = MIN_SEARCH_LENGTH,
      onChange,
      messages,
      value,
      hasSearchButton,
      searchButtonProps,
      hasBooleanChip,
      booleanChipProps,
      shouldHighlightItems = true,
      onFocus,
      setFocusOn = "input",
      focusedByDefault,
      ...rest
    },
    ref,
  ) => {
    const getBEMBlocksWith = createBemBlockBuilder([
      BASE_CLASS_NAME,
      consumerBaseClassName,
    ]);

    const fetchSearchSuggestions = useThrottledDebouncedInput(
      fetchItems ?? (() => {}),
    );

    const { groupsToRender, itemsToRender } = useGroupedBy(
      items,
      groups,
      groupItemsBy,
    );

    const handleChange = (
      changes: UseComboboxStateChange<AutocompleteItem>,
    ) => {
      const { selectedItem, ...restChanges } = changes;

      onChange &&
        onChange({
          ...restChanges,
          selectedItem: selectedItem as Item | null,
        });

      fetchSearchSuggestions(changes.inputValue ?? "");
    };

    const autocompleteProps = {
      value,
      items: itemsToRender,
      baseClassName: getBEMBlocksWith(),
      className: getBEMBlocksWith("__autocomplete-wrapper"),
      onChange: handleChange,
      itemToString: itemToString as (item?: AutocompleteItem | null) => string,
      minSearchLength,
      displayHighlightedValue,
      focusedByDefault: focusedByDefault && setFocusOn === "input",
      ...rest,
    };

    if (hasBooleanChip && !booleanChipProps) {
      throw new Error(
        "You need to pass booleanChipProps (value, messages, onChange) to SearchAutocomplete to render BooleanChip",
      );
    }

    const wrapperClassName = getWrapperClassNames({
      consumerWrapperClassName,
      consumerBaseClassName,
      defaultBaseClassName: BASE_CLASS_NAME,
    });

    return (
      <div className={wrapperClassName}>
        <Autocomplete {...autocompleteProps} ref={ref}>
          {hasBooleanChip && booleanChipProps && (
            <Adornment placement="start">
              <BooleanChip {...booleanChipProps} />
            </Adornment>
          )}
          <Autocomplete.InputGroup>
            <Autocomplete.Input
              /** Override downshift's default label system */
              aria-labelledby={undefined}
              onFocus={onFocus}
              {...messages.autocompleteMessages.input}
            />
          </Autocomplete.InputGroup>
          <Autocomplete.Menu
            isGrouped={Boolean(groupsToRender)}
            /** Override downshift's default label system */
            aria-labelledby={undefined}
            {...messages.autocompleteMessages.menu}
          >
            {groupsToRender
              ? groupsToRender.map(
                  ({ items: groupItems, ...groupProps }, groupIndex) => (
                    <Autocomplete.Group
                      {...groupProps}
                      key={`${groupProps.id}-${groupIndex}`}
                    >
                      {groupItems.map((item) => (
                        <Autocomplete.Item
                          item={item}
                          index={item.index}
                          key={`${item.term}-${item.index}`}
                        >
                          <ItemContent
                            item={item}
                            shouldHighlightItems={shouldHighlightItems}
                            getBEMBlocksWith={getBEMBlocksWith}
                          />
                        </Autocomplete.Item>
                      ))}
                    </Autocomplete.Group>
                  ),
                )
              : itemsToRender.map((item, index) => (
                  <Autocomplete.Item
                    item={item}
                    index={index}
                    key={`${item.term}-${index}`}
                  >
                    <ItemContent
                      item={item}
                      shouldHighlightItems={shouldHighlightItems}
                      getBEMBlocksWith={getBEMBlocksWith}
                    />
                  </Autocomplete.Item>
                ))}
          </Autocomplete.Menu>

          <Adornment placement="end">
            <Autocomplete.ClearButton
              {...messages.autocompleteMessages.clearButton}
            />

            {hasSearchButton && (
              <SearchButton
                {...searchButtonProps}
                focusedByDefault={
                  focusedByDefault && setFocusOn === "searchButton"
                }
              />
            )}
          </Adornment>
        </Autocomplete>
      </div>
    );
  },
);

export default SearchAutocomplete;
